/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a loose `const` inside a component.
 *
 * Reason: these values had been redeclared in ~12 files with two spellings
 * of the same number (`5 * 60 * 1000` and `300_000`), so changing
 * the cache policy meant touching all those places and risked
 * leaving divergent values. Import from here instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** Milliseconds in a minute. Base for deriving the remaining durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Minutes a cached datum is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally via PowerSync, so a generous `staleTime`
 * (5 min) avoids redundant refetches/re-renders without any real risk of
 * showing stale data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (even though it currently matches the
 * offline-cache value) because it is an independent decision: it must stay aligned
 * between `useSession`, the `_app` route guard, and `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Ceiling for waiting on a `get-session` made INSIDE a route `beforeLoad`.
 *
 * P23/CC-60 · I1-bis: a `beforeLoad` that `await`s the network without a ceiling does not fail
 * with a 401 or 503 (both resolve quickly) — it fails when the response
 * STALLS: the router does not mount the route, the `<Suspense fallback={null}>` in
 * `__root` has nothing to render, and the entire app stays BLANK for as long
 * as the network takes. MEASURED in this repo, on `/login`, as a guest and
 * cold: `#root.childElementCount` = 0 and `innerText` = 0 at 3, 6, 10, 15, and
 * 20 s, both in `vite dev` and on the PRODUCTION BUNDLE, with a CONTROL
 * in the same run that returned 1 and 327.
 *
 * SINGLE source for both route guards (`_app` and `_auth`): having it in two
 * literals was exactly what left `_auth` without the defense that `_app` did
 * have — which is why `/` was healthy and `/login` was not. After the ceiling, the
 * screen is RENDERED and the late response remains alive, syncing machine and cache;
 * `BootManager` redirects afterward if a session turns out to exist.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably longer than the browser takes to consume the href
 * of the `<a download>`; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;

/**
 * Units of measure offered in selectors (recipes, pantry).
 *
 * Standard kitchen abbreviations; they are not translated (they are universal symbols,
 * like "kg" or "ml"). A missing unit is represented with `null`
 * (the Select placeholder), not with an empty option.
 */
export const UNIT_OPTIONS = ["g", "kg", "ml", "l", "ud", "cda", "cdta"] as const;
